import axios from "axios";
import h from "../utilities/helpers";
export class Uploader {
  constructor(options) {
    this.chunkSize = 1024 * 1024 * 5;
    this.threadsQuantity = 8;
    this.file = options.file;
    this.fileName = options.file.name;
    this.aborted = false;
    this.uploadedSize = 0;
    this.progressCache = {};
    this.activeConnections = {};
    this.parts = [];
    this.uploadedParts = [];
    this.fileId = null;
    this.fileKey = null;
    this.onProgressFn = options.handleProgress;
    this.onErrorFn = options.handleError;
    this.handleComplete = options.handleComplete;
    this.privateFiles = options.privateFiles;

    this.thumbnail = options.thumbnail;
    this.initializeResponse = options.initializeResponse;
  }

  start() {
    this.initialize();
  }

  async initialize() {
    try {
      // adding the the file extension (if present) to fileName
      let fileName = this.fileName;
      let folder;

      const type = (this.file.type || h.getFileType(this.file))
        .split("/")[0]
        .toLowerCase();
      if (this.thumbnail) {
        folder = "thumbnails";
      } else {
        switch (type) {
          case "audio":
            folder = "audio";
            break;
          case "video":
            folder = "videos";
            break;
          case "image":
            folder = "images";
            break;
          default:
            folder = "other";
        }
      }

      const initializeResponse = this.initializeResponse;

      this.fileId = initializeResponse.fileId;
      this.fileKey = initializeResponse.fileKey;

      const newParts = initializeResponse.parts;
      this.parts.push(...newParts);
      this.sendNext();
    } catch (error) {
      await this.complete(error);
    }
  }

  sendNext() {
    const activeConnections = Object.keys(this.activeConnections).length;

    if (activeConnections >= this.threadsQuantity) {
      return;
    }

    if (!this.parts.length) {
      if (!activeConnections) {
        this.complete();
      }

      return;
    }

    const part = this.parts.pop();
    if (this.file && part) {
      const sentSize = (part.PartNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => {
        this.sendNext();
      };

      this.sendChunk(chunk, part, sendChunkStarted)
        .then(() => {
          this.sendNext();
        })
        .catch((error) => {
          this.parts.push(part);

          this.complete(error);
        });
    }
  }

  async complete(error) {
    if (error && !this.aborted) {
      this.onErrorFn(error);
      return;
    }

    if (error) {
      this.onErrorFn(error);
      return;
    }

    try {
      await this.sendCompleteRequest();
    } catch (error) {
      this.onErrorFn(error);
    }
  }

  async sendCompleteRequest() {
    if (this.fileId && this.fileKey) {
      const videoFinalizationMultiPartInput = {
        fileId: this.fileId,
        fileKey: this.fileKey,
        parts: this.uploadedParts,
        type: this.file.type || h.getFileType(this.file),
        privateFiles: this.privateFiles,
      };

      const completion = await axios.post(
        process.env.REACT_APP_LAMBDA_SUPPORT + "/finalize-upload",
        videoFinalizationMultiPartInput
      );

      await this.handleComplete(this.fileKey);
    }
  }

  sendChunk(chunk, part, sendChunkStarted) {
    return new Promise((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted)
        .then((status) => {
          if (status !== 200) {
            reject(new Error("Failed chunk upload"));
            return;
          }

          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  handleProgress(part, event) {
    if (this.file) {
      if (
        event.type === "progress" ||
        event.type === "error" ||
        event.type === "abort"
      ) {
        this.progressCache[part] = event.loaded;
      }

      if (event.type === "uploaded") {
        this.uploadedSize += this.progressCache[part] || 0;
        delete this.progressCache[part];
      }

      const inProgress = Object.keys(this.progressCache)
        .map(Number)
        .reduce((memo, id) => (memo += this.progressCache[id]), 0);

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size);

      const total = this.file.size;

      const percentage = Math.round((sent / total) * 100);

      this.onProgressFn({
        sent: sent,
        total: total,
        percentage: percentage,
      });
    }
  }

  upload(file, part, sendChunkStarted) {
    // uploading each part with its pre-signed URL
    return new Promise(async (resolve, reject) => {
      try {
        if (this.fileId && this.fileKey) {
          const xhr = (this.activeConnections[part.PartNumber - 1] =
            new XMLHttpRequest());

          sendChunkStarted();

          const progressListener = this.handleProgress.bind(
            this,
            part.PartNumber - 1
          );

          xhr.upload.addEventListener("progress", progressListener);

          xhr.addEventListener("error", progressListener);
          xhr.addEventListener("abort", progressListener);
          xhr.addEventListener("loadend", progressListener);

          xhr.open("PUT", part.signedUrl);

          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4 && xhr.status === 200) {
              const ETag = xhr.getResponseHeader("Etag");
              if (ETag) {
                const uploadedPart = {
                  PartNumber: part.PartNumber,
                  ETag: ETag.replaceAll('"', ""),
                };

                this.uploadedParts.push(uploadedPart);

                resolve(xhr.status);
                delete this.activeConnections[part.PartNumber - 1];
              }
            }
          };

          xhr.onerror = (error) => {
            reject(error);
            delete this.activeConnections[part.PartNumber - 1];
          };

          xhr.onabort = () => {
            reject(new Error("Upload canceled by user"));
            delete this.activeConnections[part.PartNumber - 1];
          };

          xhr.send(file);
          // }
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  onProgress(onProgress) {
    this.onProgressFn = onProgress;
    return this;
  }

  onError(onError) {
    this.onErrorFn = onError;
    return this;
  }

  abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach((id) => {
        this.activeConnections[id].abort();
      });

    this.aborted = true;
  }
}
